import { postGameByGameIdEndMutation } from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  toast,
} from "@win-reality/react-ui";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { setTrackingState } from "../../store/slices/socket";
import { setModalState } from "../../store/slices/view";
import type { RootState } from "../../store/store";
import { handleErrorNotification } from "../../utils/handleErrorNotification";
import { endGameEffects } from "./ActiveGameModal/helpers";

const viewLabels: Record<string, string> = {
  "Game Mode": "Game",
  "Bullpen Mode": "Bullpen",
  "Batting Practice Mode": "Batting Practice",
};

export const EndSessionModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { state } = useSelector((state: RootState) => state.view.modalState);
  const activeView = useSelector((state: RootState) => state.view.activeView);
  const sessionUuid = useSelector((state: RootState) => state.session.uuid);

  const isOpen = state === "End Session";
  const sessionLabel = viewLabels[activeView] ?? "Session";

  const handleClose = () => {
    dispatch(setModalState({ state: null, data: {} }));
    dispatch(setTrackingState(true));
  };

  const { mutationFn } = postGameByGameIdEndMutation();
  const { mutate: endGameMutation, isPending } = useMutation({
    mutationFn,
    onSuccess: () => {
      endGameEffects(dispatch);
      handleClose();
      toast(`The ${sessionLabel.toLowerCase()} has been ended!`, {
        variant: "success",
      });
      navigate(".");
    },
    onError: (error: Error) =>
      handleErrorNotification(
        `An error occurred while trying to end the ${sessionLabel.toLowerCase()}`,
        error,
      ),
  });

  const handleConfirm = () => {
    endGameMutation({
      body: {},
      path: {
        game_id: sessionUuid,
      },
    });
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleClose}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>{`End ${sessionLabel}?`}</DialogTitle>
            </DialogHeader>

            <p className="text-text-light">
              Are you sure you want to end the current{" "}
              {sessionLabel.toLowerCase()}? This action cannot be undone.
            </p>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                onClick={handleConfirm}
                isDisabled={isPending}
                isLoading={isPending}
              >
                {`End ${sessionLabel}`}
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
